// The buyer's closing page: "Köpet är genomfört" — the mockup's DealClosing, buyer side.
//
// The last page of the buyer's journey, after Försäkra bilen. A DEMO surface like that page: every
// value (car, price, parties, dates, documents) is hard-coded in the front end and nothing is read
// from or saved to the backend. The only thing that travels in is what the buyer did about
// insurance, which the previous page hands over in memory.
//
// TRUTH. The ownership transfer is only ever REPORTED (by the seller); Transportstyrelsen registers
// the owner. Nothing here says the buyer is the registered owner.
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusDot } from "../StatusMark";
import { DocumentMark } from "../icons";
import RegistrationPlate from "../../common/RegistrationPlate";
import { H1_CLASS, LEAD_CLASS, META_CLASS } from "../payment/PaymentParts";
import { OWNERSHIP_CONTENT_MAX } from "../ownership/OwnershipParts";
import { DEMO_SELLER_NAME, DEMO_TRANSFER_DATE, DEMO_VEHICLE } from "./insuranceDemoData";

export const DEMO_PRICE_SEK = 359000;
const DEMO_SIGNED_DATE = "2026-07-18";

const SV = {
  eyebrow: "Genomförd affär",
  title: "Köpet är genomfört",
  lead: "Du har köpt {vehicle} av {seller}.",
  price: "Pris",
  "row.ownership": "Ägarbyte",
  "row.insurance": "Försäkring",
  ownership: "Anmält av säljaren",
  "ownership.note": "Transportstyrelsen hanterar registreringen.",
  "ins.own": "Egen försäkring · hanteras utanför Klargo",
  "ins.fallback": "Tecknad via Klargo · finns under Fordon",
  "ins.policy": "{level} hos {insurer}",
  "ins.monthly": "{price} kr/mån",
  "ins.from": "gäller från {date}",
  "cover.trafik": "Trafikförsäkring",
  "cover.halv": "Halvförsäkring",
  "cover.hel": "Helförsäkring",
  documents: "Dokument",
  contract: "Köpekontrakt",
  "contract.meta": "Signerat av båda parter med BankID",
  "contract.view": "Visa avtalet",
  receipt: "Affärskvitto",
  created: "Skapat {date}",
  view: "Visa",
  close: "Stäng",
  previewNote: "Exempeldokument för demonstration.",
  primary: "Visa mitt fordon",
  secondary: "Visa affären",
  parties: "Parter",
  seller: "Säljare",
  buyer: "Köpare",
  vehicle: "Fordon",
  reg: "Registreringsnummer",
};
const EN = {
  eyebrow: "Completed transaction",
  title: "The purchase is complete",
  lead: "You have bought {vehicle} from {seller}.",
  price: "Price",
  "row.ownership": "Change of ownership",
  "row.insurance": "Insurance",
  ownership: "Reported by the seller",
  "ownership.note": "Transportstyrelsen (the Swedish Transport Agency) handles the registration.",
  "ins.own": "Own insurance · handled outside Klargo",
  "ins.fallback": "Taken out through Klargo · listed under Vehicles",
  "ins.policy": "{level} with {insurer}",
  "ins.monthly": "SEK {price}/month",
  "ins.from": "valid from {date}",
  "cover.trafik": "Third-party liability",
  "cover.halv": "Partial cover",
  "cover.hel": "Comprehensive cover",
  documents: "Documents",
  contract: "Purchase contract",
  "contract.meta": "Signed by both parties with BankID",
  "contract.view": "View the contract",
  receipt: "Transaction receipt",
  created: "Created {date}",
  view: "View",
  close: "Close",
  previewNote: "Sample document for demonstration.",
  primary: "View my vehicle",
  secondary: "View the transaction",
  parties: "Parties",
  seller: "Seller",
  buyer: "Buyer",
  vehicle: "Vehicle",
  reg: "Registration number",
};

const DEMO_BUYER_NAME = "Erik Johansson";

function DocumentEntry({ title, meta, openLabel, onOpen, testId }) {
  return (
    <div className="-mx-2.5" data-testid="document-entry">
      <button type="button" data-testid={testId} onClick={onOpen}
        className="group flex w-full min-w-0 items-start gap-[13px] rounded-[10px] px-2.5 py-3 text-left transition-colors hover:bg-[rgba(20,36,46,.045)]">
        <span className="flex w-[27px] flex-none justify-center pt-[2px]"><DocumentMark width={18} height={21} /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold leading-[1.3] text-ds-ink">{title}</span>
          <span className="ds-meta mt-[2px] block font-normal leading-[1.4]">{meta}</span>
          <span className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold leading-[1.3] text-ds-ink transition-colors group-hover:text-ds-green">
            <span className="ds-underline">{openLabel}</span>
            <span aria-hidden="true">→</span>
          </span>
        </span>
      </button>
    </div>
  );
}

/**
 * `insurance` is what the previous page handed over: { kind: "signed", level, insurer, monthlySek,
 * startDate } | { kind: "own" } | null (a reload: this browser holds nothing, so no policy is
 * invented and the row is left out).
 */
export default function DealPurchaseClosing({ insurance, onNavigate, recordPath, vehiclesPath }) {
  const { i18n } = useTranslation();
  const language = i18n.language && i18n.language.startsWith("en") ? "en" : "sv";
  const locale = language === "en" ? "en-GB" : "sv-SE";
  const table = language === "en" ? EN : SV;
  const c = (key, values = {}) =>
    String(table[key] ?? SV[key] ?? key).replace(/\{([a-z]+)\}/g, (_, k) => (values[k] !== undefined ? values[k] : `{${k}}`));
  const date = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  const [preview, setPreview] = useState(null);

  const vehicleName = `${DEMO_VEHICLE.make} ${DEMO_VEHICLE.model}`;
  const price = `${DEMO_PRICE_SEK.toLocaleString("sv-SE")} kr`;

  const insuranceRow = useMemo(() => {
    if (!insurance) return null;
    if (insurance.kind === "own") return { text: c("ins.own") };
    const policy = [
      c("ins.policy", { level: c(`cover.${insurance.level}`), insurer: insurance.insurer }),
      insurance.monthlySek !== undefined ? c("ins.monthly", { price: String(insurance.monthlySek) }) : null,
    ].filter(Boolean).join(" · ");
    return { text: policy, date: c("ins.from", { date: date(insurance.startDate) }) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurance, language]);

  // Only what is still in motion: Transportstyrelsen registers the owner, and the buyer's cover.
  const rows = [
    { key: "ownership", label: c("row.ownership"), text: c("ownership"), note: c("ownership.note") },
    ...(insuranceRow ? [{ key: "insurance", label: c("row.insurance"), ...insuranceRow }] : []),
  ];

  const openDoc = (kind) => setPreview(kind);
  const previewTitle = preview === "receipt" ? c("receipt") : c("contract");

  return (
    <>
      <div className="mx-auto w-full pb-8" style={{ maxWidth: OWNERSHIP_CONTENT_MAX }} data-testid="deal-closing" data-role="buyer">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-t-2 border-ds-ink pt-4">
          <RegistrationPlate value={DEMO_VEHICLE.registrationNumber} size="row" />
          <div className="text-right">
            <div className="text-[12.5px] font-medium text-ds-muted">{c("price")}</div>
            <div className="mt-0.5 text-[17px] font-semibold tabular-nums text-ds-ink">{price}</div>
          </div>
        </div>

        <div className="mb-2 flex items-center gap-2" data-testid="closing-kicker">
          <StatusDot tone="green" />
          <span className="text-[13px] font-semibold tracking-[0.02em] text-ds-muted">
            {c("eyebrow")} · {date(DEMO_TRANSFER_DATE)}
          </span>
        </div>
        <h1 className={H1_CLASS}>{c("title")}</h1>
        <p className={LEAD_CLASS} data-testid="closing-lead">{c("lead", { vehicle: vehicleName, seller: DEMO_SELLER_NAME })}</p>

        <dl className="m-0 mt-6 border-t border-[rgba(20,36,46,.25)]" data-testid="closing-ledger">
          {rows.map((row) => (
            <div key={row.key} data-testid={`closing-row-${row.key}`}
              className="border-b border-ds-rule-row py-3 sm:grid sm:grid-cols-[9.5rem_1fr] sm:gap-x-6 sm:py-3.5">
              <dt className="ds-label">{row.label}</dt>
              <dd className="m-0 mt-0.5 text-[15px] font-medium leading-[1.45] text-ds-ink sm:mt-0">
                {row.text}
                {row.date && (
                  <span className="font-normal text-ds-muted">
                    <span className="hidden sm:inline"> · </span>
                    <span className="block sm:inline">{row.date}</span>
                  </span>
                )}
                {row.note && <span className={`${META_CLASS} mt-0.5 block font-normal`}>{row.note}</span>}
              </dd>
            </div>
          ))}
        </dl>

        <section className="mt-8" aria-labelledby="closing-documents-title" data-testid="closing-documents">
          <h2 id="closing-documents-title" className="m-0 mb-1 text-[15px] font-semibold text-ds-ink">{c("documents")}</h2>
          <div className="border-t border-[rgba(20,36,46,.25)]">
            <div className="border-b border-ds-rule-row" data-testid="closing-contract">
              <DocumentEntry testId="closing-contract-open" title={c("contract")}
                meta={`${c("contract.meta")} · ${date(DEMO_SIGNED_DATE)}`} openLabel={c("contract.view")}
                onOpen={() => openDoc("contract")} />
            </div>
            <div className="border-b border-ds-rule-row" data-testid="closing-receipt">
              <DocumentEntry testId="closing-receipt-open" title={c("receipt")}
                meta={`Klargo · ${c("created", { date: date(DEMO_TRANSFER_DATE) })}`} openLabel={c("view")}
                onOpen={() => openDoc("receipt")} />
            </div>
          </div>
        </section>

        <div className="mt-8 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-7">
          <button type="button" onClick={() => onNavigate(vehiclesPath)} data-testid="closing-primary"
            className="ds-action-ink w-full justify-center sm:w-auto sm:min-w-[220px]">
            {c("primary")}
          </button>
          <button type="button" onClick={() => onNavigate(recordPath)} data-testid="closing-secondary"
            className="ds-action-text min-h-[44px] justify-center">
            <span className="ds-underline">{c("secondary")}</span>
          </button>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,36,46,.45)] p-4" role="presentation"
          onClick={() => setPreview(null)}>
          <div role="dialog" aria-modal="true" aria-label={previewTitle} data-testid="closing-preview"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-[12px] border border-ds-rule-edge bg-ds-sheet p-6">
            <p className="m-0 text-[17px] font-semibold text-ds-ink">{previewTitle}</p>
            <dl className="m-0 mt-4 border-t border-ds-rule-row">
              {[
                [c("vehicle"), `${vehicleName} ${DEMO_VEHICLE.year}`],
                [c("reg"), DEMO_VEHICLE.registrationNumber],
                [c("price"), price],
                [c("seller"), DEMO_SELLER_NAME],
                [c("buyer"), DEMO_BUYER_NAME],
              ].map(([label, value]) => (
                <div key={label} className="flex flex-wrap items-baseline justify-between gap-x-6 border-b border-ds-rule-row py-2.5">
                  <dt className="ds-label m-0">{label}</dt>
                  <dd className="m-0 text-[14px] font-semibold text-ds-ink">{value}</dd>
                </div>
              ))}
            </dl>
            <p className={`${META_CLASS} mt-4`}>{c("previewNote")}</p>
            <div className="mt-5">
              <button type="button" className="ds-action-outline ds-action-compact" onClick={() => setPreview(null)}>{c("close")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
